import axios from "axios";

// Surface the API's error message (e.g. backend validation / guard messages such
// as the usable-image floor or empty-pool gate) to the user, falling back when
// the response has no usable string message.
export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  const message = (data as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
};

// The api's words when the writer cannot be reached. Other 503s can carry a bare
// code as their message, so this one is matched exactly.
const WRITER_BUSY_MESSAGE =
  "We're currently experiencing an issue, please try again later.";

// Only a refusal (4xx) carries a reason written for the user; a 5xx body
// ("Internal server error") or a network failure keeps the caller's text.
export const getApiRefusalMessage = (
  error: unknown,
  fallback: string,
): string => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 503) {
    return getApiErrorMessage(error, fallback) === WRITER_BUSY_MESSAGE
      ? WRITER_BUSY_MESSAGE
      : fallback;
  }
  return status !== undefined && status >= 400 && status < 500
    ? getApiErrorMessage(error, fallback)
    : fallback;
};

// MB-1424: the api rejects a manual add of the user's OWN listing while
// Autopilot is on, since auto-ingest brings those in by itself. Keyed on the
// code rather than the message so copy changes can't break the check.
export const isAutopilotOwnListingBlock = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === "AUTOPILOT_OWN_LISTING";
};

// MB-1506: the api refuses a manual add of one of the user's OWN listings whose
// property type is not checked under Property Types to Auto-Sync AND which is not
// staged in the MLS review lists. Nothing brings that listing in on its own, so
// the plain own-listing dialog would be a dead end. Keyed on the code, like its
// neighbours, so copy changes can't break the check.
export const isAutopilotTypeNotSyncedBlock = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === "AUTOPILOT_TYPE_NOT_SYNCED";
};

// The settings-picker label for the type that is switched off ("Commercial (Lease)"),
// so the dialog can name it. Null when the api couldn't resolve one - a type the
// picker doesn't offer - and the caller falls back to generic wording.
export const getBlockedPropertyTypeLabel = (error: unknown): string | null => {
  const data = axios.isAxiosError(error) ? error.response?.data : undefined;
  const label = (data as { propertyTypeLabel?: unknown } | undefined)
    ?.propertyTypeLabel;
  return typeof label === "string" && label.length > 0 ? label : null;
};

// MB-1430: the api refuses a listing whose MLS forbids internet display
// (InternetEntireListingDisplayYN = N). The preview overlay normally blocks
// before this is ever reachable; this catches direct/stale submissions.
export const isMlsDisplayBlock = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === "MLS_INTERNET_DISPLAY_BLOCKED";
};

// MB-1441: the api refuses manually adding an Active Under Contract listing
// while Autopilot is on — autopilot never markets one, so it would strand
// unmarketable with manual campaigns blocked. The preview overlay normally
// blocks first; this catches direct/stale submissions.
export const isAutopilotUnderContractBlock = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === "AUTOPILOT_UNDER_CONTRACT_LISTING";
};

// MB-1441: manual listing-campaign operations (create, RentCast conversion)
// are refused wholesale while Autopilot is on. Callers should explain that,
// not coach a retry the api will refuse again.
export const isAutopilotManagedBlock = (error: unknown): boolean => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) {
    return false;
  }
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === "AUTOPILOT_MANAGED_LISTING";
};

// The api answers 503 with one of these codes while the MLS feed is down or
// returned an incomplete record. Returns the api's plain-language message, or
// null for any other error.
export const getMlsFeedOutageMessage = (error: unknown): string | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 503) {
    return null;
  }
  const data = error.response?.data as
    | { code?: unknown; message?: unknown }
    | undefined;
  if (
    data?.code !== "MLS_FEED_UNAVAILABLE" &&
    data?.code !== "MLS_FEED_INCOMPLETE_RECORD"
  ) {
    return null;
  }
  return typeof data.message === "string" && data.message
    ? data.message
    : "Our MLS data provider is currently unavailable. Please try again in a little while.";
};
